<template>
  <v-card flat rounded="lg" class="items-table-card">
    <!-- Table Header -->
    <v-card-title class="items-table-card__header px-5 py-3">
      <div class="d-flex align-center w-full">
        <div class="d-flex align-center">
          <v-icon color="indigo" class="mr-2">mdi-package-variant</v-icon>
          <span class="subtitle-1 font-weight-bold">Line Items</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="teal"
          depressed
          small
          class="text-none mr-2"
          dark
          @click="showBundleDialog = true"
        >
          <v-icon left small>mdi-package-variant-closed</v-icon>
          Add Bundle
        </v-btn>
        <v-btn
          color="primary"
          depressed
          small
          class="text-none"
          @click="addRow"
        >
          <v-icon left small>mdi-plus</v-icon>
          Add Item
        </v-btn>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <!-- Empty State -->
    <div
      v-if="localItems.length === 0 && bundles.length === 0"
      class="items-table-card__empty pa-8 text-center"
    >
      <v-icon size="64" color="grey lighten-1" class="mb-3"
        >mdi-package-variant-plus</v-icon
      >
      <div class="text-h6 grey--text text--lighten-1 mb-1">No items yet</div>
      <div class="body-2 grey--text mb-4">
        Add standalone items or create bundles to start building the quotation.
      </div>
      <div class="d-flex justify-center">
        <v-btn
          color="teal"
          depressed
          class="text-none mr-3"
          dark
          @click="showBundleDialog = true"
        >
          <v-icon left small>mdi-package-variant-closed</v-icon>
          Add Bundle
        </v-btn>
        <v-btn color="primary" depressed class="text-none" @click="addRow">
          <v-icon left small>mdi-plus</v-icon>
          Add First Item
        </v-btn>
      </div>
    </div>

    <!-- Items Table -->
    <div v-else class="items-table-card__table-wrapper">
      <v-simple-table class="items-table items-table--fixed-layout" dense>
        <template v-slot:default>
          <colgroup>
            <col style="width: 36px" />
            <col style="width: 36px" />
            <col
              v-for="col in resizableColumns"
              :key="'col-' + col.key"
              :style="{ width: columnWidths[col.key] + 'px' }"
            />
            <col style="width: 44px" />
          </colgroup>
          <thead>
            <tr class="items-table__header-row">
              <th class="text-center col-fixed" style="width: 36px">
                <v-icon x-small color="grey">mdi-drag</v-icon>
              </th>
              <th class="text-center col-fixed" style="width: 36px">#</th>
              <th
                v-for="col in resizableColumns"
                :key="'th-' + col.key"
                :class="[col.thClass || '', 'col-resizable']"
                :style="{ width: columnWidths[col.key] + 'px' }"
              >
                {{ col.label }}
                <div
                  class="col-resize-handle"
                  @mousedown.prevent="onResizeStart($event, col.key)"
                ></div>
              </th>
              <th class="col-fixed" style="width: 44px"></th>
            </tr>
          </thead>

          <!-- ===== STANDALONE ITEMS (draggable) ===== -->
          <draggable
            v-model="localItems"
            tag="tbody"
            handle=".drag-handle"
            :animation="200"
            :group="{ name: 'quotation-items', pull: true, put: true }"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            @change="onStandaloneChange"
          >
            <tr
              v-for="(item, index) in localItems"
              :key="'standalone-' + item._uid"
              class="items-table__row"
              :class="{ 'items-table__row--alt': index % 2 === 1 }"
            >
              <td class="text-center">
                <v-icon small class="drag-handle" color="grey lighten-1"
                  >mdi-drag-vertical</v-icon
                >
              </td>
              <td class="text-center caption grey--text text--darken-1">
                {{ index + 1 }}
              </td>
              <td>
                <v-text-field
                  v-model="item.item_type"
                  dense
                  hide-details
                  solo
                  flat
                  class="items-table__input"
                  background-color="transparent"
                  placeholder="e.g. Hardware"
                  @change="emitUpdate()"
                ></v-text-field>
              </td>
              <td>
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  dense
                  hide-details
                  solo
                  flat
                  min="1"
                  class="items-table__input items-table__input--number"
                  background-color="transparent"
                  @input="calculateItem(index)"
                ></v-text-field>
              </td>
              <td>
                <v-select
                  v-model="item.unit"
                  :items="unitTypes"
                  dense
                  hide-details
                  solo
                  flat
                  class="items-table__input"
                  background-color="transparent"
                  @change="emitUpdate()"
                ></v-select>
              </td>
              <td>
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="item.item_name"
                    dense
                    hide-details
                    solo
                    flat
                    class="items-table__input"
                    background-color="transparent"
                    placeholder="Enter item name"
                    @change="emitUpdate()"
                  ></v-text-field>
                  <v-btn
                    icon
                    x-small
                    color="indigo"
                    class="ml-1"
                    title="Pick from inventory"
                    @click="openItemPicker('standalone', index)"
                  >
                    <v-icon x-small>mdi-database-search</v-icon>
                  </v-btn>
                </div>
              </td>
              <td>
                <v-text-field
                  v-model="item.supplier"
                  dense
                  hide-details
                  solo
                  flat
                  class="items-table__input"
                  background-color="transparent"
                  placeholder="e.g. Supplier name"
                  @change="emitUpdate()"
                ></v-text-field>
              </td>
              <td>
                <v-text-field
                  v-model.number="item.supplier_price"
                  type="number"
                  dense
                  hide-details
                  solo
                  flat
                  min="0"
                  class="items-table__input items-table__input--number"
                  background-color="transparent"
                  @input="calculateItem(index)"
                ></v-text-field>
              </td>
              <td class="text-center">
                <v-checkbox
                  v-model="item.is_vat"
                  dense
                  hide-details
                  class="mt-0 pt-0 d-inline-flex"
                  color="primary"
                  @change="calculateItem(index)"
                ></v-checkbox>
              </td>
              <td>
                <v-text-field
                  v-model.number="item.freight_in_per_unit"
                  type="number"
                  dense
                  hide-details
                  solo
                  flat
                  min="0"
                  class="items-table__input items-table__input--number"
                  background-color="transparent"
                  @input="calculateItem(index)"
                ></v-text-field>
              </td>
              <td class="text-right">
                <span class="caption grey--text text--darken-2">
                  {{ formatNumber(item.final_supplier_price) }}
                </span>
              </td>
              <td class="text-right">
                <span class="caption grey--text text--darken-2">
                  {{ formatNumber(item.final_quote_price_per_unit) }}
                </span>
              </td>
              <td class="text-right">
                <span class="body-2 font-weight-bold primary--text">
                  {{ formatNumber(item.final_quote_price_total) }}
                </span>
              </td>
              <td class="text-center">
                <v-btn
                  icon
                  x-small
                  color="red lighten-1"
                  @click="removeRow(index)"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </td>
            </tr>
          </draggable>

          <!-- ===== BUNDLE SECTIONS (header + items + subtotal together per bundle) ===== -->
          <template v-for="(bundle, bIdx) in bundles">
            <!-- Bundle Header Row -->
            <tbody :key="'bh-' + bundle.id">
              <tr class="bundle-header-row">
                <td :colspan="13" class="pa-0">
                  <div class="d-flex align-center px-3 py-2 bundle-header">
                    <v-icon
                      small
                      color="white"
                      class="mr-1 bundle-drag-handle"
                      style="cursor: grab"
                      >mdi-drag-vertical</v-icon
                    >
                    <span
                      class="body-2 font-weight-bold white--text mr-2"
                      style="min-width: 20px"
                      >{{ localItems.length + bIdx + 1 }}.</span
                    >
                    <v-icon small color="white" class="mr-2"
                      >mdi-package-variant-closed</v-icon
                    >
                    <span class="body-2 font-weight-bold white--text">{{
                      bundle.name
                    }}</span>
                    <v-chip
                      x-small
                      label
                      dark
                      color="teal darken-2"
                      class="ml-2"
                      >{{ bundle.type }}</v-chip
                    >
                    <v-spacer></v-spacer>
                    <span class="caption white--text mr-3">
                      Subtotal: {{ formatCurrency(getBundleSubtotal(bIdx)) }}
                    </span>
                    <v-btn
                      icon
                      x-small
                      dark
                      @click="showRenameBundleDialog(bIdx)"
                      class="mr-1"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      dark
                      @click="saveBundleAsTemplate(bIdx)"
                      class="mr-1"
                    >
                      <v-icon small>mdi-content-save</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      dark
                      @click="addItemToBundle(bIdx)"
                      class="mr-1"
                    >
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                    <v-btn icon x-small dark @click="removeBundle(bIdx)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td class="pa-0 bundle-header-edge"></td>
              </tr>
            </tbody>

            <!-- Bundle Item Rows (draggable) -->
            <draggable
              :key="'bd-' + bundle.id"
              v-model="bundle.items"
              tag="tbody"
              handle=".drag-handle"
              :animation="200"
              :group="{ name: 'quotation-items', pull: true, put: true }"
              ghost-class="drag-ghost"
              chosen-class="drag-chosen"
              @change="onBundleItemChange"
            >
              <tr
                v-for="(bItem, iIdx) in bundle.items"
                :key="'bi-' + bItem._uid"
                class="items-table__row bundle-item-row"
              >
                <td class="text-center bundle-left-border">
                  <v-icon small class="drag-handle" color="grey lighten-1"
                    >mdi-drag-vertical</v-icon
                  >
                </td>
                <td class="text-center caption grey--text text--darken-1">
                  {{ iIdx + 1 }}
                </td>
                <td>
                  <v-text-field
                    v-model="bItem.item_type"
                    dense
                    hide-details
                    solo
                    flat
                    class="items-table__input"
                    background-color="transparent"
                    placeholder="e.g. Hardware"
                    @change="emitUpdate()"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model.number="bItem.quantity"
                    type="number"
                    dense
                    hide-details
                    solo
                    flat
                    min="1"
                    class="items-table__input items-table__input--number"
                    background-color="transparent"
                    @input="calculateBundleItem(bIdx, iIdx)"
                  ></v-text-field>
                </td>
                <td>
                  <v-select
                    v-model="bItem.unit"
                    :items="unitTypes"
                    dense
                    hide-details
                    solo
                    flat
                    class="items-table__input"
                    background-color="transparent"
                    @change="emitUpdate()"
                  ></v-select>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <v-text-field
                      v-model="bItem.item_name"
                      dense
                      hide-details
                      solo
                      flat
                      class="items-table__input"
                      background-color="transparent"
                      placeholder="Enter item name"
                      @change="emitUpdate()"
                    ></v-text-field>
                    <v-btn
                      icon
                      x-small
                      color="indigo"
                      class="ml-1"
                      title="Pick from inventory"
                      @click="openItemPicker('bundle', bIdx, iIdx)"
                    >
                      <v-icon x-small>mdi-database-search</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td>
                  <v-text-field
                    v-model="bItem.supplier"
                    dense
                    hide-details
                    solo
                    flat
                    class="items-table__input"
                    background-color="transparent"
                    placeholder="e.g. Supplier name"
                    @change="emitUpdate()"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model.number="bItem.supplier_price"
                    type="number"
                    dense
                    hide-details
                    solo
                    flat
                    min="0"
                    class="items-table__input items-table__input--number"
                    background-color="transparent"
                    @input="calculateBundleItem(bIdx, iIdx)"
                  ></v-text-field>
                </td>
                <td class="text-center">
                  <v-checkbox
                    v-model="bItem.is_vat"
                    dense
                    hide-details
                    class="mt-0 pt-0 d-inline-flex"
                    color="primary"
                    @change="calculateBundleItem(bIdx, iIdx)"
                  ></v-checkbox>
                </td>
                <td>
                  <v-text-field
                    v-model.number="bItem.freight_in_per_unit"
                    type="number"
                    dense
                    hide-details
                    solo
                    flat
                    min="0"
                    class="items-table__input items-table__input--number"
                    background-color="transparent"
                    @input="calculateBundleItem(bIdx, iIdx)"
                  ></v-text-field>
                </td>
                <td class="text-right">
                  <span class="caption grey--text text--darken-2">
                    {{ formatNumber(bItem.final_supplier_price) }}
                  </span>
                </td>
                <td class="text-right">
                  <span class="caption grey--text text--darken-2">
                    {{ formatNumber(bItem.final_quote_price_per_unit) }}
                  </span>
                </td>
                <td class="text-right">
                  <span class="body-2 font-weight-bold primary--text">
                    {{ formatNumber(bItem.final_quote_price_total) }}
                  </span>
                </td>
                <td class="text-center">
                  <v-btn
                    icon
                    x-small
                    color="red lighten-1"
                    @click="removeItemFromBundle(bIdx, iIdx)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </draggable>

            <!-- Bundle Subtotal Row -->
            <tbody :key="'bs-' + bundle.id">
              <tr class="bundle-subtotal-row">
                <td colspan="13" class="text-right pr-4 bundle-left-border">
                  <span
                    class="caption font-weight-bold teal--text text--darken-2"
                  >
                    {{ bundle.name }} Subtotal
                  </span>
                </td>
                <td class="text-right">
                  <span
                    class="caption font-weight-bold teal--text text--darken-2"
                  >
                    {{ formatCurrency(getBundleSubtotal(bIdx)) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </template>

          <!-- Footer / Running Total -->
          <tfoot>
            <tr class="items-table__footer-row">
              <td colspan="13" class="text-right pr-4">
                <span class="subtitle-2 font-weight-bold">Running Total</span>
              </td>
              <td class="text-right">
                <span class="subtitle-2 font-weight-bold primary--text">
                  {{ formatCurrency(runningTotal) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </template>
      </v-simple-table>
    </div>

    <!-- Quick Add Footer -->
    <v-divider v-if="localItems.length > 0 || bundles.length > 0"></v-divider>
    <div
      v-if="localItems.length > 0 || bundles.length > 0"
      class="px-5 py-3 d-flex align-center"
    >
      <v-btn text small color="primary" class="text-none mr-2" @click="addRow">
        <v-icon left small>mdi-plus</v-icon>
        Add Item
      </v-btn>
      <v-btn
        text
        small
        color="teal"
        class="text-none"
        @click="showBundleDialog = true"
      >
        <v-icon left small>mdi-package-variant-closed</v-icon>
        Add Bundle
      </v-btn>
      <v-spacer></v-spacer>
      <span class="caption grey--text">
        {{ totalItemCount }} item{{ totalItemCount !== 1 ? "s" : "" }}
        <template v-if="bundles.length > 0">
          in {{ bundles.length }} bundle{{ bundles.length !== 1 ? "s" : "" }} +
          {{ localItems.length }} standalone
        </template>
      </span>
    </div>

    <!-- Bundle Add Dialog -->
    <bundle-add-dialog :show.sync="showBundleDialog" @create="onBundleCreate" />

    <!-- Save Bundle Template Dialog -->
    <save-bundle-template-dialog
      :show.sync="showSaveTemplateDialog"
      :bundle="templateBundle"
      @saved="onTemplateSaved"
    />

    <!-- Rename Bundle Dialog -->
    <v-dialog v-model="renameDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold"
          >Rename Bundle</v-card-title
        >
        <v-card-text>
          <v-text-field
            v-model="renameName"
            label="Bundle Name"
            outlined
            dense
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="renameDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            depressed
            class="text-none"
            @click="confirmRename"
            >Rename</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Item Picker Dialog -->
    <item-picker-dialog :show.sync="showItemPicker" @select="onItemPicked" />
  </v-card>
</template>

<script>
import draggable from "vuedraggable";
import BundleAddDialog from "./BundleAddDialog.vue";
import SaveBundleTemplateDialog from "./SaveBundleTemplateDialog.vue";
import ItemPickerDialog from "./ItemPickerDialog.vue";

export default {
  components: {
    draggable,
    BundleAddDialog,
    SaveBundleTemplateDialog,
    ItemPickerDialog,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    markupPercentage: {
      type: Number,
      default: 0,
    },
    vatAdjustmentPercentage: {
      type: Number,
      default: 15,
    },
    quotationMode: {
      type: String,
      default: "walk_in",
    },
  },
  data() {
    return {
      localItems: [],
      bundles: [],
      unitTypes: ["pc", "set", "unit", "lot", "pack", "roll", "meter", "box"],
      showBundleDialog: false,
      showSaveTemplateDialog: false,
      templateBundle: null,
      showItemPicker: false,
      pickerContext: null,
      renameDialog: false,
      renameName: "",
      renameBundleIndex: -1,
      // Column resize state
      resizableColumns: [
        {
          key: "type",
          label: "Type",
          defaultWidth: 110,
          minWidth: 80,
          thClass: "",
        },
        {
          key: "qty",
          label: "Qty",
          defaultWidth: 65,
          minWidth: 50,
          thClass: "",
        },
        {
          key: "unit",
          label: "Unit",
          defaultWidth: 75,
          minWidth: 60,
          thClass: "",
        },
        {
          key: "item_name",
          label: "Item Name",
          defaultWidth: 160,
          minWidth: 100,
          thClass: "",
        },
        {
          key: "supplier",
          label: "Supplier",
          defaultWidth: 150,
          minWidth: 100,
          thClass: "",
        },
        {
          key: "supplier_price",
          label: "Supplier Price",
          defaultWidth: 110,
          minWidth: 80,
          thClass: "",
        },
        {
          key: "vat",
          label: "VAT?",
          defaultWidth: 60,
          minWidth: 50,
          thClass: "text-center",
        },
        {
          key: "freight",
          label: "Freight/Unit",
          defaultWidth: 100,
          minWidth: 80,
          thClass: "",
        },
        {
          key: "adj_price",
          label: "Adj. Price",
          defaultWidth: 110,
          minWidth: 80,
          thClass: "text-right",
        },
        {
          key: "quote_unit",
          label: "Quote/Unit",
          defaultWidth: 110,
          minWidth: 80,
          thClass: "text-right",
        },
        {
          key: "total",
          label: "Total",
          defaultWidth: 110,
          minWidth: 80,
          thClass: "text-right",
        },
      ],
      columnWidths: {},
      resizeState: null,
    };
  },
  computed: {
    runningTotal() {
      const standaloneTotal = this.localItems.reduce(
        (sum, item) => sum + (parseFloat(item.final_quote_price_total) || 0),
        0
      );
      const bundleTotal = this.bundles.reduce((sum, bundle) => {
        return (
          sum +
          bundle.items.reduce(
            (s, item) => s + (parseFloat(item.final_quote_price_total) || 0),
            0
          )
        );
      }, 0);
      return standaloneTotal + bundleTotal;
    },
    totalItemCount() {
      const bundleItemCount = this.bundles.reduce(
        (sum, b) => sum + b.items.length,
        0
      );
      return this.localItems.length + bundleItemCount;
    },
  },
  created() {
    this.initColumnWidths();
  },
  beforeDestroy() {
    // Clean up any lingering resize listeners
    document.removeEventListener("mousemove", this.onResizeMove);
    document.removeEventListener("mouseup", this.onResizeEnd);
  },
  watch: {
    items: {
      handler(val) {
        if (
          val &&
          val.length > 0 &&
          this.localItems.length === 0 &&
          this.bundles.length === 0
        ) {
          this.reconstructFromFlatItems(val);
          this.recalculateAll();
        }
      },
      immediate: true,
    },
    markupPercentage() {
      this.recalculateAll();
    },
    vatAdjustmentPercentage() {
      this.recalculateAll();
    },
    quotationMode() {
      this.recalculateAll();
    },
  },
  methods: {
    // ===== Column Resize =====
    initColumnWidths() {
      const saved = this.loadColumnWidths();
      const widths = {};
      this.resizableColumns.forEach((col) => {
        widths[col.key] =
          saved && saved[col.key] ? saved[col.key] : col.defaultWidth;
      });
      this.columnWidths = widths;
    },
    loadColumnWidths() {
      try {
        const raw = localStorage.getItem("jmb_quotation_column_widths");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    saveColumnWidths() {
      try {
        localStorage.setItem(
          "jmb_quotation_column_widths",
          JSON.stringify(this.columnWidths)
        );
      } catch {
        // localStorage may be unavailable
      }
    },
    onResizeStart(event, colKey) {
      const col = this.resizableColumns.find((c) => c.key === colKey);
      if (!col) return;
      this.resizeState = {
        colKey: colKey,
        startX: event.clientX,
        startWidth: this.columnWidths[colKey],
        minWidth: col.minWidth,
      };
      document.addEventListener("mousemove", this.onResizeMove);
      document.addEventListener("mouseup", this.onResizeEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    onResizeMove(event) {
      if (!this.resizeState) return;
      const delta = event.clientX - this.resizeState.startX;
      const newWidth = Math.max(
        this.resizeState.minWidth,
        this.resizeState.startWidth + delta
      );
      this.$set(this.columnWidths, this.resizeState.colKey, newWidth);
    },
    onResizeEnd() {
      if (this.resizeState) {
        this.saveColumnWidths();
        this.resizeState = null;
      }
      document.removeEventListener("mousemove", this.onResizeMove);
      document.removeEventListener("mouseup", this.onResizeEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },

    // ===== Shared Calculation =====
    _calculateSingleItem(item) {
      const supplierPrice = parseFloat(item.supplier_price) || 0;
      const qty = parseFloat(item.quantity) || 0;
      const freightIn = parseFloat(item.freight_in_per_unit) || 0;
      const markup = parseFloat(this.markupPercentage) || 0;
      const vatAdj = parseFloat(this.vatAdjustmentPercentage) || 0;

      // Step 1: Final Supplier Price
      item.final_supplier_price = item.is_vat
        ? supplierPrice
        : supplierPrice * (1 + vatAdj / 100);

      // Step 2: Apply markup to supplier price only
      const markedUpPrice = item.final_supplier_price * (1 + markup / 100);

      // Step 3: Add freight at cost
      const subtotal = markedUpPrice + freightIn;

      // Step 4: Government VAT Out
      item.final_quote_price_per_unit =
        this.quotationMode === "government" ? subtotal * 1.12 : subtotal;

      // Cost per unit
      item.cost_per_unit = item.final_supplier_price + freightIn;

      // Total
      item.final_quote_price_total = item.final_quote_price_per_unit * qty;
      item.total_amount = item.final_quote_price_total;

      return item;
    },

    // ===== Unique ID Generator =====
    _generateItemUid() {
      return (
        "_item_" +
        Date.now().toString(36) +
        Math.random().toString(36).substr(2, 8)
      );
    },

    // ===== Standalone Items =====
    createEmptyItem() {
      return {
        _uid: this._generateItemUid(),
        item_type: "Hardware",
        quantity: 1,
        unit: "pc",
        item_name: "",
        supplier: "",
        supplier_price: 0,
        is_vat: false,
        freight_in_per_unit: 0,
        final_supplier_price: 0,
        cost_per_unit: 0,
        final_quote_price_per_unit: 0,
        final_quote_price_total: 0,
        total_amount: 0,
      };
    },
    addRow() {
      this.localItems.push(this.createEmptyItem());
    },
    removeRow(index) {
      this.localItems.splice(index, 1);
      this.emitUpdate();
    },
    calculateItem(index) {
      const item = this.localItems[index];
      this._calculateSingleItem(item);
      this.$set(this.localItems, index, item);
      this.emitUpdate();
    },

    // ===== Bundle Methods =====
    generateBundleId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2, 6);
    },
    addBundle(name, type) {
      this.bundles.push({
        id: this.generateBundleId(),
        name: name,
        type: type,
        items: [this.createEmptyItem()],
      });
      this.emitUpdate();
    },
    loadTemplateAsBundle(template, name) {
      const bundleId = this.generateBundleId();
      const items = (template.items || []).map((tItem) => {
        const item = {
          _uid: this._generateItemUid(),
          item_type: tItem.item_type || "Hardware",
          quantity: parseInt(tItem.quantity) || 1,
          unit: tItem.unit || "pc",
          item_name: tItem.item_name || "",
          supplier: tItem.supplier || "",
          supplier_price: parseFloat(tItem.supplier_price) || 0,
          is_vat: tItem.is_vat || false,
          freight_in_per_unit: parseFloat(tItem.freight_in_per_unit) || 0,
          final_supplier_price: 0,
          cost_per_unit: 0,
          final_quote_price_per_unit: 0,
          final_quote_price_total: 0,
          total_amount: 0,
        };
        this._calculateSingleItem(item);
        return item;
      });
      this.bundles.push({
        id: bundleId,
        name: name || template.name,
        type: template.bundle_type,
        items: items.length > 0 ? items : [this.createEmptyItem()],
      });
      this.emitUpdate();
    },
    addItemToBundle(bundleIndex) {
      this.bundles[bundleIndex].items.push(this.createEmptyItem());
    },
    removeItemFromBundle(bundleIndex, itemIndex) {
      const bundle = this.bundles[bundleIndex];
      if (bundle.items.length <= 1) {
        this.removeBundle(bundleIndex);
      } else {
        bundle.items.splice(itemIndex, 1);
        this.emitUpdate();
      }
    },
    removeBundle(bundleIndex) {
      this.bundles.splice(bundleIndex, 1);
      this.emitUpdate();
    },

    // ===== Drag-and-Drop Handlers =====
    // NOTE: vuedraggable v2 emits the 'change' event inside $nextTick,
    // so by the time these handlers run, the data arrays have already been
    // updated and Vue has re-rendered. This avoids conflicts with Sortable.js
    // DOM operations that caused items to disappear when using @end/@add events.
    onStandaloneChange(evt) {
      // Handle items added to standalone from a bundle
      if (evt.added) {
        // Clear any bundle fields on the moved item
        const item = evt.added.element;
        delete item.bundle_id;
        delete item.bundle_name;
        delete item.bundle_type;
        delete item.bundle_sort_order;
      }
      this.cleanEmptyBundles();
      this.emitUpdate();
    },
    onBundleReorder() {
      this.emitUpdate();
    },
    onBundleItemChange() {
      // Handles reorder within a bundle, items added from standalone,
      // and items added from other bundles.
      this.cleanEmptyBundles();
      this.emitUpdate();
    },
    cleanEmptyBundles() {
      // Remove bundles that have no items left, using splice to avoid
      // replacing the entire array reference (which disrupts Vue reactivity
      // and Sortable.js internal state)
      for (let i = this.bundles.length - 1; i >= 0; i--) {
        if (!this.bundles[i].items || this.bundles[i].items.length === 0) {
          this.bundles.splice(i, 1);
        }
      }
    },

    calculateBundleItem(bundleIndex, itemIndex) {
      const item = this.bundles[bundleIndex].items[itemIndex];
      this._calculateSingleItem(item);
      this.$set(this.bundles[bundleIndex].items, itemIndex, item);
      this.emitUpdate();
    },
    getBundleSubtotal(bundleIndex) {
      return this.bundles[bundleIndex].items.reduce(
        (sum, item) => sum + (parseFloat(item.final_quote_price_total) || 0),
        0
      );
    },

    // ===== Bundle Dialog Handlers =====
    onBundleCreate({ name, type, template }) {
      if (template) {
        this.loadTemplateAsBundle(template, name);
      } else {
        this.addBundle(name, type);
      }
    },
    showRenameBundleDialog(bundleIndex) {
      this.renameBundleIndex = bundleIndex;
      this.renameName = this.bundles[bundleIndex].name;
      this.renameDialog = true;
    },
    confirmRename() {
      if (this.renameBundleIndex >= 0 && this.renameName.trim()) {
        this.bundles[this.renameBundleIndex].name = this.renameName.trim();
        this.emitUpdate();
      }
      this.renameDialog = false;
    },
    saveBundleAsTemplate(bundleIndex) {
      this.templateBundle = this.bundles[bundleIndex];
      this.showSaveTemplateDialog = true;
    },
    onTemplateSaved() {
      // Template saved, no further action needed
    },

    // ===== Reconstruct from flat items (edit mode) =====
    reconstructFromFlatItems(flatItems) {
      const standalone = [];
      const bundleMap = {};

      flatItems.forEach((item) => {
        const mapped = { ...item };
        // Ensure every item has a unique _uid for stable Vue keys
        if (!mapped._uid) {
          mapped._uid = this._generateItemUid();
        }
        if (mapped.bundle_id) {
          if (!bundleMap[mapped.bundle_id]) {
            bundleMap[mapped.bundle_id] = {
              id: mapped.bundle_id,
              name: mapped.bundle_name || "Bundle",
              type: mapped.bundle_type || "Custom",
              items: [],
            };
          }
          bundleMap[mapped.bundle_id].items.push(mapped);
        } else {
          standalone.push(mapped);
        }
      });

      // Sort bundle items by bundle_sort_order
      Object.values(bundleMap).forEach((bundle) => {
        bundle.items.sort(
          (a, b) => (a.bundle_sort_order || 0) - (b.bundle_sort_order || 0)
        );
      });

      this.localItems = standalone;
      this.bundles = Object.values(bundleMap);
    },

    // ===== Recalculate & Emit =====
    recalculateAll() {
      for (let i = 0; i < this.localItems.length; i++) {
        this._calculateSingleItem(this.localItems[i]);
        this.$set(this.localItems, i, this.localItems[i]);
      }
      for (let b = 0; b < this.bundles.length; b++) {
        for (let i = 0; i < this.bundles[b].items.length; i++) {
          this._calculateSingleItem(this.bundles[b].items[i]);
          this.$set(this.bundles[b].items, i, this.bundles[b].items[i]);
        }
      }
      this.emitUpdate();
    },
    emitUpdate() {
      const allItems = [];

      // Standalone items (no bundle fields)
      this.localItems.forEach((item) => {
        const { _uid: _omit1, ...rest } = item; // eslint-disable-line no-unused-vars
        allItems.push({
          ...rest,
          bundle_id: null,
          bundle_name: null,
          bundle_type: null,
          bundle_sort_order: 0,
        });
      });

      // Bundle items (with bundle fields)
      this.bundles.forEach((bundle) => {
        bundle.items.forEach((item, idx) => {
          const { _uid: _omit2, ...rest } = item; // eslint-disable-line no-unused-vars
          allItems.push({
            ...rest,
            bundle_id: bundle.id,
            bundle_name: bundle.name,
            bundle_type: bundle.type,
            bundle_sort_order: idx,
          });
        });
      });

      this.$emit("update", allItems);
    },

    // ===== Item Picker =====
    openItemPicker(type, indexOrBundleIdx, itemIdx) {
      if (type === "standalone") {
        this.pickerContext = { type: "standalone", index: indexOrBundleIdx };
      } else {
        this.pickerContext = {
          type: "bundle",
          bundleIndex: indexOrBundleIdx,
          itemIndex: itemIdx,
        };
      }
      this.showItemPicker = true;
    },
    onItemPicked(data) {
      if (!this.pickerContext) return;
      const ctx = this.pickerContext;

      if (ctx.type === "standalone") {
        const item = this.localItems[ctx.index];
        this.$set(item, "item_name", data.item_name);
        this.$set(item, "item_type", data.item_type);
        this.$set(item, "unit", data.unit);
        this.$set(item, "supplier", data.supplier);
        this.$set(item, "supplier_price", data.supplier_price);
        this.$set(item, "is_vat", data.is_vat);
        this.$set(item, "freight_in_per_unit", data.freight_in_per_unit);
        this._calculateSingleItem(item);
        this.$set(this.localItems, ctx.index, item);
      } else {
        const bItem = this.bundles[ctx.bundleIndex].items[ctx.itemIndex];
        this.$set(bItem, "item_name", data.item_name);
        this.$set(bItem, "item_type", data.item_type);
        this.$set(bItem, "unit", data.unit);
        this.$set(bItem, "supplier", data.supplier);
        this.$set(bItem, "supplier_price", data.supplier_price);
        this.$set(bItem, "is_vat", data.is_vat);
        this.$set(bItem, "freight_in_per_unit", data.freight_in_per_unit);
        this._calculateSingleItem(bItem);
        this.$set(this.bundles[ctx.bundleIndex].items, ctx.itemIndex, bItem);
      }

      this.emitUpdate();
      this.pickerContext = null;
    },

    // ===== Formatting =====
    formatNumber(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return (
        "₱ " +
        num.toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.w-full {
  width: 100%;
}

.items-table-card {
  border: 1px solid #e8eaed;
  overflow: hidden;

  &__header {
    background: #fafbfc;
  }

  &__empty {
    background: #fafbfc;
  }

  &__table-wrapper {
    overflow-x: auto;
  }
}

.items-table {
  /* Fixed table layout for reliable column widths */
  &--fixed-layout {
    ::v-deep table {
      table-layout: fixed;
      width: 100%;
    }
  }

  /* Header row */
  &__header-row {
    th {
      background: #f5f6fa !important;
      color: #5f6368 !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
      padding: 10px 8px !important;
      border-bottom: 2px solid #e0e0e0 !important;
      border-right: 1px solid #d0d4da !important;
      overflow: hidden;
      text-overflow: ellipsis;

      &:last-child {
        border-right: none !important;
      }
    }

    /* Resizable column header */
    th.col-resizable {
      position: relative;
    }

    /* Fixed (non-resizable) columns */
    th.col-fixed {
      position: relative;
    }
  }

  /* Data rows */
  &__row {
    transition: background-color 0.15s ease;

    td {
      padding: 4px 6px !important;
      vertical-align: middle;
      border-bottom: 1px solid #f0f0f0 !important;
      border-right: 1px solid #e8eaed !important;

      &:last-child {
        border-right: none !important;
      }
    }

    &:hover {
      background-color: #e8f0fe !important;
    }

    &--alt {
      background-color: #fafbfc;
    }
  }

  /* Footer / totals row */
  &__footer-row {
    td {
      padding: 12px 8px !important;
      background: #f5f6fa;
      border-top: 2px solid #e0e0e0 !important;
    }
  }

  /* Data rows: ensure cell content respects column widths */
  &__row td {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Input styling inside table */
  &__input {
    font-size: 12px !important;

    &--number {
      max-width: 100%;
    }

    ::v-deep .v-input__slot {
      min-height: 30px !important;
      padding: 0 6px !important;
      background: transparent !important;
      box-shadow: none !important;

      &:hover {
        background: #f0f4ff !important;
        border-radius: 4px;
      }

      &:focus-within {
        background: #e8f0fe !important;
        border-radius: 4px;
      }
    }

    ::v-deep .v-select__selections,
    ::v-deep input {
      font-size: 12px !important;
    }
  }
}

/* Bundle header row */
.bundle-header-row {
  td {
    background: transparent !important;
    padding: 0 !important;
    border-bottom: none !important;
  }
}

.bundle-header {
  background: linear-gradient(135deg, #00897b, #00695c);
  min-height: 38px;
}

.bundle-header-edge {
  background: linear-gradient(135deg, #00897b, #00695c);
}

/* Bundle item rows - left border indicator */
.bundle-item-row {
  .bundle-left-border {
    border-left: 3px solid #00897b !important;
  }
}

/* Drag handle */
.drag-handle {
  cursor: grab;
  opacity: 0.4;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }
}

.bundle-drag-handle {
  cursor: grab;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }
}

/* Drag ghost (placeholder shown while dragging) */
.drag-ghost {
  opacity: 0.3;
  background: #e3f2fd !important;

  td {
    background: #e3f2fd !important;
  }
}

.drag-chosen {
  background: #e8f0fe !important;

  td {
    background: #e8f0fe !important;
  }
}

.bundle-drag-ghost {
  opacity: 0.3;
  background: #e0f2f1 !important;
}

/* Bundle subtotal row */
.bundle-subtotal-row {
  td {
    padding: 6px 8px !important;
    background: #e0f2f1 !important;
    border-bottom: 2px solid #b2dfdb !important;
  }

  .bundle-left-border {
    border-left: 3px solid #00897b !important;
  }
}

/* Column resize handle */
.col-resize-handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: 7px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
  background-color: rgba(25, 118, 210, 0.15);
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(25, 118, 210, 0.45);
  }

  &:active {
    background-color: rgba(25, 118, 210, 0.6);
  }
}
</style>
